import { ToolTip } from "./toolTip";
import * as d3 from "d3";
import { ZoomBehavior } from "d3";

export abstract class InteractiveMapBase {
    protected svg: d3.Selection<SVGElement, any, HTMLElement, any>;
    protected zoom: ZoomBehavior<any, any>;
    protected id: string;
    protected transform: d3.ZoomTransform;
    private timeout: NodeJS.Timeout | undefined;

    constructor(selector: d3.Selection<SVGElement, any, HTMLElement, any>) {
        this.svg = selector;
        this.zoom = d3.zoom();
        this.id = selector.attr("id");
        this.transform = new d3.ZoomTransform(1, 0, 0);
    }

    setUpZoomingBehavior(callback: () => void, zoomMin = 1, zoomMax = 8) {
        const width: number = Number.parseFloat(this.background.attr("width"));
        const height: number = Number.parseFloat(
            this.background.attr("height")
        );

        this.svg.attr("preserveAspectRatio", "xMinYMin meet");

        this.zoom
            .scaleExtent([zoomMin, zoomMax])
            .translateExtent([
                [0, 0],
                [width, height],
            ])
            .on("zoom", (event) => {
                if (typeof callback === "function") callback();
                this.transform = event.transform as d3.ZoomTransform;
                this.applyZoomTransform();
            });

        this.svg.call(this.zoom);
    }

    protected applyZoomTransform() {
        const transform = this.transform.toString();
        this.circles?.attr("transform", transform);
        this.background.attr("transform", transform);
    }

    protected zoomOut(): Promise<void> {
        const transform = new d3.ZoomTransform(1, 0, 0).toString();
        const transition = d3.transition().duration(350).ease(d3.easeCircle);

        const ps = [
            this.circles
                ?.transition(transition)
                .attr("transform", transform)
                .end(),
            this.background
                .transition(transition)
                .attr("transform", transform)
                .end(),
        ];

        return new Promise((resolve) => Promise.all(ps).then(() => resolve()));
    }

    setHoverDelay(tooltip: ToolTip, timer: number = 300) {
        tooltip.selector.on("mouseover", () => {
            tooltip.showLast();
            this.clearFadeTimeout();
        });

        tooltip.selector.on("mouseout", () => {
            this.clearFadeTimeout();
        });

        this.background.on("mouseout", () => {
            this.clearFadeTimeout();
            this.timeout = setTimeout(() => tooltip.hide(), timer);
        });

        this.background.on("mouseover", () => {
            this.clearFadeTimeout();
            this.timeout = setTimeout(() => tooltip.hide(), timer);
        });
    }

    clearFadeTimeout() {
        clearTimeout(this.timeout);
    }

    select(selector: any) {
        return this.svg.select(selector);
    }

    selectAll(selector: any) {
        return this.svg.selectAll(selector);
    }

    get circles() {
        const circles = this.svg.selectAll("circle");
        return circles.empty() ? undefined : circles;
    }

    circle(id: number): d3.Selection<SVGCircleElement, any, any, any> {
        return this.svg.select(`#cp${id}`);
    }

    get ids() {
        const circleIds: number[] = [];
        this.circles?.each(function () {
            const id = d3.select(this).attr("id").replace("cp", "");
            circleIds.push(Number(id));
        });

        return circleIds;
    }

    get background() {
        return this.svg.select("image");
    }

    get width() {
        return Number(this.background.attr("width").replace("px", ""));
    }

    baseInit(tooltip: ToolTip) {
        tooltip.clean();
        this.setHoverDelay(tooltip);
        this.setUpZoomingBehavior(() => tooltip.hide());
        this.circles?.attr("cursor", "pointer");
    }
}
